/**
 * AB#38014 - pure decision helper for "a request document is attached, but intake never
 * started and never resolved."
 *
 * Why this exists: the Quick Entry placeholder ("Add a request document to start Quick
 * Entry") is gated ONLY on intake status - isQuickEntryAwaitingIntake() is
 * `!isIntakeResolved() && !isIntakeAnalysisRunning()` and never looks at documentsList. So
 * ANY path that leaves both intake statuses at their initial values - the extraction trigger
 * never firing, a whole-slice state reset, an upload whose continuation never ran - parks the
 * pane on a message that says "add a document" while a document is demonstrably attached,
 * permanently, with no way forward. Quick Entry has no manual Extract button, so the only
 * escape is navigating away and back.
 *
 * Rather than add a new bespoke affordance, a confirmed stuck state is reported as the intake
 * failure it effectively is: the caller raises the SAME "couldn't be analyzed - re-attach or
 * use Classic Mode" message any other retryable failure raises, and the pane stops hiding the
 * rows (a failure counts as resolved), so the specialist gets the recovery options they
 * already know.
 *
 * The settle delay is what keeps this honest. In the normal flow the extraction POST is
 * dispatched moments after the upload succeeds, and dispatching it sets API_FETCHING
 * synchronously - so "document present, nothing running" is a real state for only a brief
 * instant. Declaring failure immediately would misreport that instant as a failure. The
 * caller only treats the transaction as stuck once this has been continuously true for
 * INTAKE_STUCK_SETTLE_MS.
 */
export interface IntakeStuckGateInputs {
    /** DataEntryDetails.isQuickEntryEligible() - only Quick Entry shows intake state at all. */
    readonly quickEntryActive: boolean;
    /** getTransactionDocumentsAPIStatus === API_SUCCESS. An unloaded list means "unknown", not "empty". */
    readonly documentsLoaded: boolean;
    /** Is a REQ document actually attached to this transaction? */
    readonly hasRequestDocument: boolean;
    /** DataEntryDetails.isIntakeResolved() - Completed / Failed / RetryExceeded / either fetch API_FAILURE. */
    readonly intakeResolved: boolean;
    /** DataEntryDetails.isIntakeAnalysisRunning() - either fetch in flight, or server reports InProgress. */
    readonly intakeRunning: boolean;
    /**
     * True when the analysis currently in the (transaction-unkeyed) intake slice belongs to a
     * DIFFERENT transaction. Ownership is unknowable-but-irrelevant here: a foreign analysis
     * tells us nothing about THIS transaction, so it must not suppress a genuine stuck state,
     * and equally must not be read as this transaction's own progress.
     */
    readonly foreignAnalysis?: boolean;
}

/** How long the stuck condition must hold continuously before it is believed. */
export const INTAKE_STUCK_SETTLE_MS = 15000;

/**
 * Is this transaction a CANDIDATE for the stuck state right now? The caller is responsible for
 * requiring that this stays true for INTAKE_STUCK_SETTLE_MS before acting on it.
 */
export function isIntakeStuckCandidate(inputs: IntakeStuckGateInputs): boolean {
    if (!inputs.quickEntryActive) {
        return false;
    }
    // An unloaded documents list cannot prove a document is attached.
    if (!inputs.documentsLoaded || !inputs.hasRequestDocument) {
        return false;
    }
    // Something is in flight, or a terminal outcome already exists - not stuck either way.
    if (inputs.intakeRunning) {
        return false;
    }
    if (inputs.intakeResolved && !inputs.foreignAnalysis) {
        return false;
    }
    return true;
}

/**
 * Should an ALREADY-CONFIRMED stuck state be torn down?
 *
 * Deliberately NOT `!isIntakeStuckCandidate(...)`. The candidate test requires
 * `documentsLoaded`, which is `getTransactionDocumentsAPIStatus === API_SUCCESS` and therefore
 * drops to false for the duration of EVERY documents refresh - including the one this bug's own
 * fix dispatches after each upload, and the 5-second Web-Print/Scan poll. Treating that as "no
 * longer stuck" made the pane flip back to the placeholder mid-refresh, unmounting the row list
 * (losing the Patient/Requestor rows' in-progress match state, the very thing the pane is
 * deliberately kept mounted to preserve) and re-arming the announcement. With a poll faster than
 * the settle delay, the rows could never appear at all.
 *
 * A refresh in flight is "temporarily unknown", not "resolved". Only a DEFINITIVE change tears
 * the state down: intake actually started, intake actually reached a terminal outcome for THIS
 * transaction, Quick Entry is no longer the surface, or the documents list has LOADED and
 * definitively contains no request document.
 *
 * That last clause matters (review round 2, M1). Ignoring documentsLoaded is right; ignoring
 * hasRequestDocument as well was not. Without it a confirmed stuck state has no way to end
 * except intake starting - which a document-less transaction never does - so it survives a
 * transaction change and renders empty editable rows, instead of the placeholder, on every
 * subsequent transaction for the rest of the session. It also left the pane wrong after the
 * request document was deleted. Requiring documentsLoaded here is what keeps the mid-refresh
 * dip from being mistaken for "the document is gone".
 */
export function shouldClearConfirmedIntakeStuck(inputs: IntakeStuckGateInputs): boolean {
    if (!inputs.quickEntryActive) {
        return true;
    }
    if (inputs.intakeRunning) {
        return true;
    }
    if (inputs.intakeResolved && !inputs.foreignAnalysis) {
        return true;
    }
    // Loaded AND empty is definitive: there is nothing to be stuck on.
    if (inputs.documentsLoaded && !inputs.hasRequestDocument) {
        return true;
    }
    return false;
}
